/**
 * @file block-undo.ts
 * @description Обратимые действия с блоками страницы для «Отменить /
 * Вернуть» (phase19.16 Р2; uiux-design-r2 §9; контракт §16.2).
 *
 * Отмена — обратный вызов API (Р3а: действует на черновик страницы). Удалённый блок
 * возвращается новым блоком с тем же содержимым на прежнее место — id у
 * него новый. Поэтому у открытой страницы одна таблица «id блока → ячейка»
 * (`BlockCells`): все записи истории держат ячейку, а не id; после возврата
 * ячейка получает новый id и новый `updatedAt`, и это видят все записи
 * (правка X → удалить X → отменить → отменить снова правит возвращённый X).
 *
 * Каждая правка блока идёт с `expectedUpdatedAt` из ячейки (409
 * `block_changed`, если блок изменили в другом месте), ответ обновляет
 * ячейку, а сохранённые блоки (и ответ перестановки — сервер при ней
 * переписывает все блоки страницы) уходят в состояние страницы
 * (`applyBlocks`), иначе следующая правка получила бы ложный 409.
 * `updatedAt` ячейки никогда не уменьшается (сравнение по времени).
 * Место вставки: `afterId` — id блока выше, `null` — первым на
 * странице (сервер ставит в конец, здесь переставляем первым).
 * Неподдерживаемый блок (другой тип из данных) пересоздать нельзя (400
 * `block_type_retired`) — его удаление в историю не попадает.
 */

import type { SectionPayload, SiteFontRegistry } from "@chaiberi/shared";

import { ApiError } from "../api-client";
import { sitePages, type BlockAppearance, type SavedSiteBlock, type SitePageBlock } from "../api-site-pages";
import type { UndoEntry } from "../../hooks/site-builder/useUndoStack";
import { isSectionBlock } from "../../components/site-pages/block-meta";
import { hasMissingFonts, stripMissingFonts } from "./element-typo";

/** Ячейка блока: id и `updatedAt` меняются при возврате и сохранении. */
export interface BlockRef {
  id: string;
  updatedAt: string;
}

/** Таблица ячеек открытой страницы. */
export interface BlockCells {
  /** Ячейка блока (создаётся при первом обращении). */
  refFor: (id: string, updatedAt?: string) => BlockRef;
  /** Блок пересоздан — новый id ведёт в ту же ячейку. */
  rekey: (ref: BlockRef, id: string, updatedAt: string) => void;
  /** Свежая страница с сервера — `updatedAt` всех известных ячеек. */
  sync: (blocks: ReadonlyArray<Pick<SitePageBlock, "id" | "updatedAt">>) => void;
}

/** `a` новее `b` (ISO-время; пустое — самое старое). */
export function newer(a: string, b: string): boolean {
  if (!a) return false;
  if (!b) return true;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return Number.isNaN(ta) || Number.isNaN(tb) ? a > b : ta > tb;
}

export function createBlockCells(): BlockCells {
  const map = new Map<string, BlockRef>();
  return {
    refFor(id, updatedAt) {
      let ref = map.get(id);
      if (!ref) {
        ref = { id, updatedAt: updatedAt ?? "" };
        map.set(id, ref);
      } else if (updatedAt && newer(updatedAt, ref.updatedAt)) {
        ref.updatedAt = updatedAt;
      }
      return ref;
    },
    rekey(ref, id, updatedAt) {
      ref.id = id;
      ref.updatedAt = updatedAt;
      map.set(id, ref);
    },
    sync(blocks) {
      for (const block of blocks) {
        const ref = map.get(block.id);
        if (!ref) map.set(block.id, { id: block.id, updatedAt: block.updatedAt });
        else if (ref.id === block.id && newer(block.updatedAt, ref.updatedAt)) ref.updatedAt = block.updatedAt;
      }
    },
  };
}

/** Снимок секции для возврата. */
export interface SectionSnapshot {
  payload: SectionPayload;
  appearance: BlockAppearance | null;
  isEnabled: boolean;
}

export function snapshotOf(block: SitePageBlock): SectionSnapshot | null {
  if (!isSectionBlock(block)) return null;
  return {
    payload: structuredClone(block.payload) as SectionPayload,
    appearance: block.appearance ? structuredClone(block.appearance) : null,
    isEnabled: block.isEnabled,
  };
}

/**
 * Записать правку блока по ячейке и обновить её `updatedAt`; сохранённый
 * блок возвращается (его кладут в состояние страницы). Ячейка без
 * `updatedAt` — без проверки (пустое значение сервер не принимает).
 */
export async function patchByRef(
  ref: BlockRef,
  input: { payload?: SectionPayload; appearance?: BlockAppearance | null; isEnabled?: boolean },
): Promise<SavedSiteBlock> {
  const saved = await sitePages.updateBlock(ref.id, { ...input, ...(ref.updatedAt ? { expectedUpdatedAt: ref.updatedAt } : {}) });
  if (newer(saved.updatedAt, ref.updatedAt) || !ref.updatedAt) ref.updatedAt = saved.updatedAt;
  return saved;
}

/** Окружение записей истории открытой страницы. */
export interface HistoryEnv {
  pageId: string;
  cells: BlockCells;
  /** Порядок блоков сейчас (id), для перестановок. */
  currentOrder: () => string[];
  /** Перечитать страницу и канву. */
  refresh: () => Promise<void>;
  /** Блоки из ответа сервера (перестановка, сохранение) — в состояние страницы и ячейки. */
  applyBlocks: (blocks: readonly SitePageBlock[]) => void;
  /**
   * Реестр шрифтов с полученным списком своих (`null` — не получен). SB120
   * этап 3а: пересоздание блока снимает удалённые свои шрифты — сервер
   * считает все ключи новыми и ответил бы 422 `element_font_not_found`.
   */
  fontRegistry?: () => SiteFontRegistry | null;
}

/** Новый блок первым: сервер ставит в конец — переставляем. */
async function moveFirst(env: HistoryEnv, id: string): Promise<void> {
  const others = env.currentOrder().filter((x) => x !== id);
  if (others.length > 0) env.applyBlocks(await sitePages.reorderBlocks(env.pageId, [id, ...others]));
}

/** Заново создать секцию после `afterId` (`null` — первой; сосед исчез — в конец). */
export async function recreateSection(env: HistoryEnv, ref: BlockRef, snap: SectionSnapshot, afterId: string | null): Promise<void> {
  const anchor = afterId ? env.cells.refFor(afterId).id : null;
  const registry = env.fontRegistry?.() ?? null;
  const payload = registry && hasMissingFonts(snap.payload, registry) ? stripMissingFonts(snap.payload, registry) : snap.payload;
  const input = { payload, appearance: snap.appearance, afterBlockId: anchor };
  let created;
  try {
    created = await sitePages.addSection(env.pageId, input);
  } catch (err) {
    if (!(err instanceof ApiError && err.code === "site_block_anchor_invalid")) throw err;
    created = await sitePages.addSection(env.pageId, { ...input, afterBlockId: null });
  }
  env.cells.rekey(ref, created.id, created.updatedAt);
  // Скрыть до перестановки: перестановка переписывает блоки (новый updatedAt).
  if (!snap.isEnabled) env.applyBlocks([await patchByRef(ref, { isEnabled: false })]);
  if (afterId === null) await moveFirst(env, created.id);
}

/** Добавлен блок (заготовка, пустой, копия): отмена — удалить, возврат — создать заново. */
export function addedEntry(env: HistoryEnv, label: string, ref: BlockRef, snap: SectionSnapshot, afterId: string | null): UndoEntry {
  return {
    label,
    undo: async () => {
      await sitePages.removeBlock(ref.id);
      await env.refresh();
    },
    redo: async () => {
      await recreateSection(env, ref, snap, afterId);
      await env.refresh();
    },
  };
}

/** Удалён блок: отмена — вернуть на место, возврат — удалить снова. */
export function removedEntry(env: HistoryEnv, label: string, ref: BlockRef, snap: SectionSnapshot, afterId: string | null): UndoEntry {
  const added = addedEntry(env, label, ref, snap, afterId);
  return { label, undo: added.redo, redo: added.undo };
}

/** Порядок блоков: отмена — прежний порядок (по ячейкам: учтены возвращённые блоки). */
export function reorderEntry(env: HistoryEnv, label: string, before: string[], after: string[]): UndoEntry {
  const beforeRefs = before.map((id) => env.cells.refFor(id));
  const afterRefs = after.map((id) => env.cells.refFor(id));
  const apply = async (order: BlockRef[]) => {
    const now = env.currentOrder();
    const wanted = order.map((r) => r.id).filter((id) => now.includes(id));
    env.applyBlocks(await sitePages.reorderBlocks(env.pageId, [...wanted, ...now.filter((id) => !wanted.includes(id))]));
    await env.refresh();
  };
  return { label, undo: () => apply(beforeRefs), redo: () => apply(afterRefs) };
}

/** Показ / скрытие блока. */
export function toggleEntry(env: HistoryEnv, label: string, ref: BlockRef, isEnabled: boolean): UndoEntry {
  const set = async (on: boolean) => {
    env.applyBlocks([await patchByRef(ref, { isEnabled: on })]);
    await env.refresh();
  };
  return { label, undo: () => set(!isEnabled), redo: () => set(isEnabled) };
}

type BlockContent = { payload: SectionPayload; appearance: BlockAppearance | null };

/** Сохранённая правка блока: отмена — записать прежнее содержимое. */
export function editEntry(env: HistoryEnv, label: string, ref: BlockRef, before: BlockContent, after: BlockContent): UndoEntry {
  const write = async (v: BlockContent) => {
    env.applyBlocks([await patchByRef(ref, { payload: v.payload, appearance: v.appearance ?? {} })]);
    await env.refresh();
  };
  return { label, undo: () => write(before), redo: () => write(after) };
}
